import type { RevenueByPlanDto } from '@planuze/admin-contracts';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useMetricsWidgetsContent } from '../../content/sections/metrics-widgets.js';
import { type Currency, fmtCurrency } from '../../lib/format.js';
import { ChartCard } from './chart-card.js';
import { colorAt } from './chart-palette.js';

interface Props {
  readonly data: RevenueByPlanDto | undefined;
  readonly loading: boolean;
  /** Moeda vem do toggle page-level (antes era estado local do chart). */
  readonly currency: Currency;
  readonly hasFilters?: boolean | undefined;
  readonly onClearFilters?: (() => void) | undefined;
}

/* Receita (MRR) por plano (donut). `revenueByPlanDto` traz MRR nas duas moedas; a moeda
   selecionada decide qual exibir. Planos com MRR 0 na moeda selecionada são omitidos. */
function sliceData(
  items: RevenueByPlanDto['items'],
  currency: Currency,
): ReadonlyArray<{ plan: string; value: number }> {
  return items
    .map((item) => ({ plan: item.plan, value: currency === 'USD' ? item.mrrUsd : item.mrrBrl }))
    .filter((slice) => slice.value > 0);
}

export function RevenueByPlanChart({ data, loading, currency, hasFilters, onClearFilters }: Props) {
  const slices = sliceData(data?.items ?? [], currency);
  const copy = useMetricsWidgetsContent().revenueByPlanChart;
  return (
    <ChartCard
      title={copy.title}
      loading={loading}
      empty={slices.length === 0}
      hasFilters={hasFilters}
      onClearFilters={onClearFilters}
    >
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={[...slices]}
            dataKey="value"
            nameKey="plan"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
          >
            {slices.map((slice, index) => (
              <Cell key={slice.plan} fill={colorAt(index)} />
            ))}
          </Pie>
          <Tooltip formatter={(value: unknown) => fmtCurrency(Number(value), currency)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
